import { useEffect, useState } from 'react';
import Avatar from '@/components/base/Avatar';
import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import Switch from '@/components/base/Switch';
import { formatCompact, formatMoney } from '@/utils/format';
import { marketFilterLabel } from '@/utils/markets';
import type { CreatorProfile as Creator } from '@/types/portal';
import { contentTypeOptions } from '../types';
import { creatorCustomAffinity, creatorHomeMarket } from '../helpers';

const PAGE_SIZES = [10, 25, 50, 100];
const PAGE_SIZE_KEY = 'twiva.recommendations.pageSize';

function savedPageSize() {
  try {
    const value = Number(localStorage.getItem(PAGE_SIZE_KEY));
    return PAGE_SIZES.includes(value) ? value : 10;
  } catch {
    return 10;
  }
}

/** "Showing 11–20 of 87", the per-page picker and page buttons. */
function Pager({
  page,
  pageCount,
  pageSize,
  total,
  onPage,
  onPageSize,
}: {
  page: number;
  pageCount: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;
}) {
  if (total === 0) return null;
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(total, page * pageSize);
  // Up to five page numbers around the current one.
  const start = Math.max(1, Math.min(page - 2, pageCount - 4));
  const pages = Array.from({ length: Math.min(5, pageCount) }, (_, i) => start + i);
  const pageButton = 'flex h-8 min-w-8 items-center justify-center rounded-md px-2 text-xs cursor-pointer disabled:cursor-not-allowed disabled:opacity-40';
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-foreground-600">
      <div className="flex items-center gap-2">
        <span>
          Showing {first}–{last} of {total}
        </span>
        <label className="flex items-center gap-1.5">
          <span className="text-foreground-400">·</span>
          <select
            value={pageSize}
            onChange={(event) => onPageSize(Number(event.target.value))}
            aria-label="Creators per page"
            className="h-8 rounded-md border border-background-300 bg-background-50 px-2 text-xs text-foreground-800 focus:border-primary-400 focus:outline-none"
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </select>
        </label>
      </div>
      {pageCount > 1 ? (
        <nav className="flex items-center gap-1" aria-label="Recommendation pages">
          <button type="button" className={`${pageButton} hover:bg-background-200`} disabled={page === 1} onClick={() => onPage(page - 1)} aria-label="Previous page">
            <i className="ri-arrow-left-s-line text-base" />
          </button>
          {pages.map((number) => (
            <button
              key={number}
              type="button"
              onClick={() => onPage(number)}
              aria-current={number === page ? 'page' : undefined}
              className={`${pageButton} ${number === page ? 'bg-primary-500 font-semibold text-background-50' : 'hover:bg-background-200'}`}
            >
              {number}
            </button>
          ))}
          <button type="button" className={`${pageButton} hover:bg-background-200`} disabled={page === pageCount} onClick={() => onPage(page + 1)} aria-label="Next page">
            <i className="ri-arrow-right-s-line text-base" />
          </button>
        </nav>
      ) : null}
    </div>
  );
}

interface RecommendationListProps {
  creators: Creator[];
  markets: string[];
  includeOtherMarkets: boolean;
  onIncludeOtherMarketsChange: (value: boolean) => void;
  contentTypes: string[];
  customContentType: string;
  inviteOnly: boolean;
  budget: number;
  currency: string;
  overBudget: number;
  selectedIds: string[];
  onToggle: (id: string) => void;
  /** Adds (true) or removes (false) these creators from the selection. */
  onSetSelected: (ids: string[], selected: boolean) => void;
  onSelectTop: () => void;
  onNext: () => void;
  onBack: () => void;
}

export default function RecommendationList({
  creators,
  markets,
  includeOtherMarkets,
  onIncludeOtherMarketsChange,
  contentTypes,
  customContentType,
  inviteOnly,
  budget,
  currency,
  overBudget,
  selectedIds,
  onToggle,
  onSetSelected,
  onSelectTop,
  onNext,
  onBack,
}: RecommendationListProps) {
  const custom = customContentType.trim();
  const marketsLabel = marketFilterLabel(markets);

  // A page at a time, so a long list needs no scrolling; the selection spans pages.
  const [pageSize, setPageSize] = useState(savedPageSize);
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(creators.length / pageSize));
  useEffect(() => {
    setPage(1);
  }, [creators.length, pageSize]);
  const currentPage = Math.min(page, pageCount);
  const offset = (currentPage - 1) * pageSize;
  const pageCreators = creators.slice(offset, offset + pageSize);
  const pageIds = pageCreators.map((creator) => creator.id);
  const pageAllSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const changePageSize = (size: number) => {
    setPageSize(size);
    try {
      localStorage.setItem(PAGE_SIZE_KEY, String(size));
    } catch {
      // Storage unavailable: the choice lasts until the page closes.
    }
  };
  const goToPage = (next: number) => {
    setPage(next);
    document.getElementById('recommendation-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const pager = (
    <Pager
      page={currentPage}
      pageCount={pageCount}
      pageSize={pageSize}
      total={creators.length}
      onPage={goToPage}
      onPageSize={changePageSize}
    />
  );

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 rounded-lg border border-accent-200 bg-accent-50 p-4">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-500 text-background-50">
          <i className="ri-sparkling-2-line" />
        </span>
        <div>
          <p className="text-sm font-medium text-accent-900">
            {creators.length} creators match this brief
          </p>
          <p className="mt-0.5 text-xs leading-relaxed text-accent-800">
            Ranked by category, market and content type fit, then weighted by delivery history.
            {custom ? (
              <>
                {' '}
                Creators whose formats relate to your custom format{' '}
                <strong>“{custom}”</strong> are boosted to the top.
              </>
            ) : null}{' '}
            Select the creators you want to shortlist — you can refine rates before inviting.
          </p>
        </div>
      </div>

      {!inviteOnly ? (
        <div className="flex items-start gap-3 rounded-lg border border-secondary-200 bg-secondary-50 p-4">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-500 text-background-50">
            <i className="ri-group-line" />
          </span>
          <div>
            <p className="text-sm font-medium text-secondary-900">Open to matching creators</p>
            <p className="mt-0.5 text-xs leading-relaxed text-secondary-800">
              This campaign is set to <strong>Open to matching creators</strong> — only creators who
              fit this brief (markets, content types and budget) can discover it and apply. It is{' '}
              not shown to the entire creator database. Anyone who matches and applies will appear in
              the campaign alongside the creators you shortlist below.
            </p>
          </div>
        </div>
      ) : null}

      {markets.length > 0 ? (
        <div className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-2.5">
            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700 text-base">
              <i className="ri-map-pin-line" />
            </span>
            <div>
              <p className="text-sm font-medium text-foreground-800">
                Showing creators based in {marketsLabel}
              </p>
              <p className="mt-0.5 text-xs leading-relaxed text-foreground-500">
                {includeOtherMarkets
                  ? `Also including creators based elsewhere who cover ${marketsLabel} — they are ranked after local creators.`
                  : 'Only creators based in your selected markets are shown. Turn this on to also see creators based elsewhere who cover them.'}
              </p>
            </div>
          </div>
          <Switch
            checked={includeOtherMarkets}
            onChange={onIncludeOtherMarketsChange}
            label="Include other markets"
          />
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-foreground-500">
          {selectedIds.length} of {creators.length} selected
        </p>
        <div className="flex flex-wrap items-center gap-1">
          {pageIds.length > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              icon={pageAllSelected ? 'ri-checkbox-indeterminate-line' : 'ri-checkbox-multiple-line'}
              onClick={() => onSetSelected(pageIds, !pageAllSelected)}
            >
              {pageAllSelected ? 'Clear this page' : 'Select this page'}
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" icon="ri-check-double-line" onClick={onSelectTop}>
            Select top 6
          </Button>
        </div>
      </div>

      <div id="recommendation-list" className="scroll-mt-24">{pager}</div>

      <div className="space-y-3">
        {creators.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-10 text-center">
            <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-background-100 text-foreground-400 text-xl">
              <i className="ri-map-pin-2-line" />
            </span>
            <p className="mt-3 text-sm font-medium text-foreground-800">
              {overBudget > 0 && budget > 0
                ? 'No creators fit this brief within budget'
                : 'No creators based in your selected markets'}
            </p>
            <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-foreground-500">
              Nothing matched {marketsLabel} with the current content types.
              {budget > 0 ? ` Every match also has a fee above your ${formatMoney(budget, currency)} budget.` : ''}
              {markets.length > 0 && !includeOtherMarkets
                ? ' Try including creators based elsewhere who cover these markets, or widen your Markets, content types or budget in the brief.'
                : ' Try widening your Markets, content types or budget in the brief.'}
            </p>
            {markets.length > 0 && !includeOtherMarkets ? (
              <div className="mt-4 flex justify-center">
                <Button variant="outline" size="sm" icon="ri-map-pin-line" onClick={() => onIncludeOtherMarketsChange(true)}>
                  Include creators based elsewhere
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
        {pageCreators.map((creator, pageIndex) => {
          const index = offset + pageIndex;
          const selected = selectedIds.includes(creator.id);
          const homeMarket = creatorHomeMarket(creator);
          const basedLocally = markets.length === 0 || markets.includes(homeMarket);
          const matched = creator.contentFormats.filter((id) => contentTypes.includes(id));
          const customFit = creatorCustomAffinity(creator, custom);
          const orderedFormats = [
            ...matched,
            ...creator.contentFormats.filter((id) => !contentTypes.includes(id)),
          ];
          return (
            <div
              key={creator.id}
              className={`rounded-lg border p-4 transition-colors ${
                selected ? 'border-primary-300 bg-primary-50' : 'border-background-200 bg-background-50'
              }`}
            >
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => onToggle(creator.id)}
                  className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border text-[12px] cursor-pointer ${
                    selected
                      ? 'border-primary-500 bg-primary-500 text-background-50'
                      : 'border-background-400 text-transparent'
                  }`}
                  aria-label={`Select ${creator.name}`}
                >
                  <i className="ri-check-line" />
                </button>
                <Avatar src={creator.avatar} alt={creator.name} size="md" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-foreground-950">
                        <span className="mr-1.5 text-xs text-foreground-400">#{index + 1}</span>
                        {creator.name}
                      </p>
                      <p className="text-xs text-foreground-500">
                        {creator.city} · {formatCompact(creator.followers)} followers ·{' '}
                        {creator.engagementRate}% eng.
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-right">
                        <span className="block text-[11px] text-foreground-400">Est. rate</span>
                        <span className="font-heading text-sm font-semibold text-foreground-900">
                          {/* Last fee agreed with this agency; production has no rate card. */}
                          {creator.rate != null ? formatMoney(creator.rate, creator.currency) : 'No fee yet'}
                        </span>
                      </span>
                      <span className="inline-flex flex-col items-center rounded-md bg-accent-100 px-2 py-1 text-accent-800">
                        <span className="font-heading text-sm font-bold leading-none">
                          {creator.fitScore ?? '—'}
                        </span>
                        <span className="text-[9px] font-label uppercase">fit</span>
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {customFit.length > 0 && custom ? (
                      <Badge tone="accent" icon="ri-magic-line">
                        Matches “{custom}”
                      </Badge>
                    ) : null}
                    {!basedLocally ? (
                      <Badge tone="primary" icon="ri-map-pin-2-line">
                        Based in {homeMarket} · covers {marketsLabel}
                      </Badge>
                    ) : null}
                    {creator.reasonCodes.slice(0, 3).map((reason) => (
                      <Badge key={reason} tone="secondary">
                        {reason}
                      </Badge>
                    ))}
                    {creator.riskFlags.map((flag) => (
                      <Badge key={flag} tone="primary" icon="ri-alert-line">
                        {flag}
                      </Badge>
                    ))}
                  </div>
                  <div className="mt-3 rounded-md bg-background-100 px-3 py-2.5">
                    <p className="mb-1.5 flex items-center gap-1 text-[11px] font-label font-medium text-foreground-500">
                      <i className="ri-shapes-line text-foreground-400" />
                      Content formats
                      {matched.length > 0 ? (
                        <span className="text-accent-700">· {matched.length} match this brief</span>
                      ) : null}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {orderedFormats.map((id) => {
                        const option = contentTypeOptions.find((type) => type.id === id);
                        if (!option) return null;
                        const isMatch = matched.includes(id);
                        return (
                          <span
                            key={id}
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${
                              isMatch
                                ? 'border-accent-300 bg-accent-50 text-accent-800'
                                : 'border-background-200 bg-background-50 text-foreground-500'
                            }`}
                          >
                            <span aria-hidden="true">{option.emoji}</span>
                            {option.label}
                            {isMatch ? <i className="ri-check-line text-accent-600" /> : null}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {pager}

      {/* Pinned to the bottom of the screen, so Review shortlist is always in reach. */}
      <div className="sticky bottom-0 z-10 -mx-1 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-background-200 bg-background-50/95 px-3 py-3 backdrop-blur">
        <Button variant="outline" icon="ri-arrow-left-line" onClick={onBack}>
          Back to brief
        </Button>
        <Button
          icon="ri-arrow-right-line"
          onClick={onNext}
          disabled={selectedIds.length === 0}
        >
          Review shortlist ({selectedIds.length})
        </Button>
      </div>
    </div>
  );
}